import React, { useState, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';
import Icons from './Icons';
import { confirmAlert } from '../utils/confirmAlert';

// Inline SVG icons for hover actions (always sharp, no emoji rendering)
const EyeIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const PenIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const BinIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
    <path d="M10 11v6M14 11v6"/>
    <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
  </svg>
);

// ── In-memory cache (persists across modal opens, same session) ──────────────
let _cache = null;        // { assets: [], ts: number }
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function isCacheFresh() {
  return _cache && (Date.now() - _cache.ts < CACHE_TTL);
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / 1048576).toFixed(1)}MB`;
}
function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const TYPE_TABS = [
  { id: 'all',   label: 'All' },
  { id: 'image', label: 'Images' },
  { id: 'video', label: 'Videos' },
  { id: 'audio', label: 'Audio' },
];

const ACCEPTED = {
  all:   'image/jpeg,image/png,image/webp,video/mp4,video/quicktime,audio/mpeg,audio/wav',
  image: 'image/jpeg,image/png,image/webp',
  video: 'video/mp4,video/quicktime',
  audio: 'audio/mpeg,audio/wav',
};

const TYPE_ICON = { image: '🖼', video: '🎬', audio: '🎵' };
const TYPE_CLR  = {
  image: 'rgba(124,58,237,0.18)',
  video: 'rgba(20,184,166,0.18)',
  audio: 'rgba(245,158,11,0.18)',
};

export default function MediaLibraryModal({
  open, onClose, onSelect,
  allowedTypes = null,
  maxSelect = 12,
  maxTypeCounts = null,
}) {
  const { token } = useAuth();

  // Use cache as initial state so UI is instant on re-open
  const [assets, setAssets]       = useState(() => _cache?.assets || []);
  const [loading, setLoading]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab]             = useState('all');
  const [selected, setSelected]   = useState(new Set());
  const [renaming, setRenaming]   = useState(null);
  const [preview, setPreview]     = useState(null);
  const [dragOver, setDragOver]   = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const fileInputRef  = useRef();
  const openedRef     = useRef(false);
  // Drag-to-select
  const isDragging    = useRef(false);
  const dragMode      = useRef('add'); // 'add' | 'remove'

  // ── Load (with cache) ─────────────────────────────────────────────────────
  const load = useCallback(async (force = false) => {
    if (!token) return;
    if (!force && isCacheFresh()) {
      setAssets(_cache.assets);
      return;
    }
    setLoading(true);
    try {
      const list = await api.fetchAssets(token);
      _cache = { assets: list, ts: Date.now() };
      setAssets(list);
    } catch {
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Only load once per open (not on every re-render)
  if (open && !openedRef.current) {
    openedRef.current = true;
    setSelected(new Set());
    load();
  }
  if (!open) {
    openedRef.current = false;
    return null;
  }

  // ── Filter ────────────────────────────────────────────────────────────────
  const effectiveTabs = allowedTypes
    ? TYPE_TABS.filter(t => t.id === 'all' || allowedTypes.includes(t.id))
    : TYPE_TABS;

  const filtered = assets.filter(a => {
    const tabOk     = tab === 'all' || a.fileType === tab;
    const allowedOk = !allowedTypes || allowedTypes.includes(a.fileType);
    return tabOk && allowedOk;
  });

  // ── Upload ────────────────────────────────────────────────────────────────
  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const res = await api.uploadAssets(token, Array.from(files));
      if (res.assets) {
        const next = [...res.assets, ...assets];
        _cache = { assets: next, ts: Date.now() };
        setAssets(next);
        toast.success(`${res.assets.length} file${res.assets.length > 1 ? 's' : ''} uploaded`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ── Select ────────────────────────────────────────────────────────────────
  const toggleSelect = (asset) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(asset.id)) { next.delete(asset.id); return next; }
      if (next.size >= maxSelect) { toast.error(`Max ${maxSelect} items`); return prev; }
      if (maxTypeCounts) {
        let cnt = 0;
        for (const id of next) { const a = assets.find(x => x.id === id); if (a?.fileType === asset.fileType) cnt++; }
        if (cnt >= maxTypeCounts[asset.fileType]) { toast.error(`Max ${maxTypeCounts[asset.fileType]} ${asset.fileType}s`); return prev; }
      }
      next.add(asset.id);
      return next;
    });
  };

  const selectAll = () => setSelected(prev => {
    const next = new Set(prev);
    for (const a of filtered) {
      if (next.has(a.id) || next.size >= maxSelect) continue;
      if (maxTypeCounts) {
        let cnt = 0;
        for (const id of next) { const x = assets.find(y => y.id === id); if (x?.fileType === a.fileType) cnt++; }
        if (cnt >= maxTypeCounts[a.fileType]) continue;
      }
      next.add(a.id);
    }
    return next;
  });

  // ── Rename ────────────────────────────────────────────────────────────────
  const saveRename = async () => {
    if (!renaming) return;
    const name = renaming.value.trim();
    if (!name) return;
    try {
      await api.renameAsset(token, renaming.id, name);
      const next = assets.map(a => a.id === renaming.id ? { ...a, fileName: name } : a);
      _cache = { assets: next, ts: Date.now() };
      setAssets(next);
      setRenaming(null);
    } catch { toast.error('Rename failed'); }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (ids) => {
    if (!ids?.length) return;
    const isConfirmed = await confirmAlert({
      title: 'Delete Items?',
      message: `Are you sure you want to delete ${ids.length} item${ids.length > 1 ? 's' : ''}? This action cannot be undone.`
    });
    if (!isConfirmed) return;

    setDeleting(true);
    try {
      await api.deleteAssets(token, ids);
      const next = assets.filter(a => !ids.includes(a.id));
      _cache = { assets: next, ts: Date.now() };
      setAssets(next);
      setSelected(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n; });
      toast.success(`${ids.length} deleted`);
    } catch { toast.error('Delete failed'); }
    finally { setDeleting(false); }
  };

  // ── Use selected ──────────────────────────────────────────────────────────
  const handleUseSelected = () => {
    const picked = assets.filter(a => selected.has(a.id));
    if (!picked.length) { toast.error('Select at least one file'); return; }
    onSelect(picked);
    onClose();
  };

  const selCount = selected.size;

  // ── Styles (shared) ───────────────────────────────────────────────────────
  const glass = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
    backdropFilter: 'blur(12px)',
  };

  return (
    <>
      {/* ── Preview overlay ── */}
      {preview && (
        <div onClick={() => setPreview(null)} style={{
          position:'fixed',inset:0,zIndex:10001,
          background:'rgba(0,0,0,0.92)',backdropFilter:'blur(12px)',
          display:'flex',alignItems:'center',justifyContent:'center',
        }}>
          <button onClick={() => setPreview(null)} style={{
            position:'absolute',top:20,right:20,width:36,height:36,borderRadius:'50%',
            background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.15)',
            color:'#fff',fontSize:16,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',
          }}>✕</button>

          {preview.fileType === 'image' && (
            <img src={preview.fileUrl} alt={preview.fileName} onClick={e=>e.stopPropagation()}
              style={{maxWidth:'88vw',maxHeight:'88vh',borderRadius:12,objectFit:'contain',boxShadow:'0 32px 80px rgba(0,0,0,0.6)'}} />
          )}
          {preview.fileType === 'video' && (
            <video src={preview.fileUrl} controls autoPlay onClick={e=>e.stopPropagation()}
              style={{maxWidth:'88vw',maxHeight:'88vh',borderRadius:12,boxShadow:'0 32px 80px rgba(0,0,0,0.6)'}} />
          )}
          {preview.fileType === 'audio' && (
            <div onClick={e=>e.stopPropagation()} style={{
              ...glass,borderRadius:20,padding:'48px 56px',
              display:'flex',flexDirection:'column',alignItems:'center',gap:24,
            }}>
              <div style={{fontSize:52,lineHeight:1}}>🎵</div>
              <div style={{color:'#fff',fontSize:14,fontWeight:600,maxWidth:260,textAlign:'center',wordBreak:'break-word'}}>{preview.fileName}</div>
              <audio src={preview.fileUrl} controls autoPlay style={{width:320,maxWidth:'80vw'}} />
            </div>
          )}
        </div>
      )}

      {/* ── Backdrop ── */}
      <div onClick={onClose} style={{
        position:'fixed',inset:0,zIndex:9000,
        background:'rgba(0,0,0,0.75)',backdropFilter:'blur(10px)',
        display:'flex',alignItems:'center',justifyContent:'center',padding:16,
      }}>
        {/* ── Panel ── */}
        <div onClick={e=>e.stopPropagation()} style={{
          width:'min(1100px,96vw)',height:'min(780px,92vh)',
          background:'linear-gradient(160deg,#111122 0%,#0c0c18 100%)',
          border:'1px solid rgba(139,92,246,0.18)',borderRadius:22,
          display:'flex',flexDirection:'column',
          boxShadow:'0 40px 100px rgba(0,0,0,0.85),0 0 0 1px rgba(139,92,246,0.06)',
          overflow:'hidden',
        }}>

          {/* ── Header ── */}
          <div style={{
            display:'flex',alignItems:'center',justifyContent:'space-between',
            padding:'14px 20px',
            borderBottom:'1px solid rgba(255,255,255,0.055)',
            background:'linear-gradient(90deg,rgba(124,58,237,0.08) 0%,transparent 100%)',
            flexShrink:0,
          }}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <div style={{
                width:32,height:32,borderRadius:9,flexShrink:0,
                background:'linear-gradient(135deg,#7c3aed,#a78bfa)',
                display:'flex',alignItems:'center',justifyContent:'center',fontSize:15,
                boxShadow:'0 4px 12px rgba(124,58,237,0.35)',
              }}>🗂</div>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:'#fff',letterSpacing:'-0.01em'}}>Media Library</div>
                <div style={{fontSize:10,color:'var(--t3)',marginTop:1}}>
                  {assets.length} file{assets.length!==1?'s':''} · max {maxSelect} selectable
                </div>
              </div>
            </div>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              {/* Refresh */}
              <button onClick={() => load(true)} title="Refresh" style={{
                width:30,height:30,borderRadius:8,cursor:'pointer',
                ...glass,color:'var(--t2)',fontSize:13,
                display:'flex',alignItems:'center',justifyContent:'center',
              }}>↻</button>
              {/* Upload */}
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{
                display:'flex',alignItems:'center',gap:5,
                padding:'6px 12px',borderRadius:8,cursor:'pointer',
                background:uploading?'rgba(124,58,237,0.3)':'linear-gradient(135deg,#7c3aed,#6d28d9)',
                border:'none',color:'#fff',fontSize:11,fontWeight:600,
                boxShadow:uploading?'none':'0 4px 14px rgba(124,58,237,0.4)',
                transition:'all 0.2s',
              }}>
                {uploading ? <><span style={{animation:'spin 1s linear infinite',display:'inline-block'}}>⟳</span> Uploading…</> : <><Icons.Upload sz={11} /> Upload</>}
              </button>
              {/* Close */}
              <button onClick={onClose} style={{
                width:30,height:30,borderRadius:8,cursor:'pointer',
                ...glass,color:'var(--t2)',fontSize:13,
                display:'flex',alignItems:'center',justifyContent:'center',
              }}>✕</button>
            </div>
          </div>

          {/* ── Tab bar ── */}
          <div style={{
            display:'flex',alignItems:'center',justifyContent:'space-between',
            padding:'8px 20px',
            borderBottom:'1px solid rgba(255,255,255,0.04)',
            flexShrink:0,gap:8,
          }}>
            <div style={{display:'flex',gap:3}}>
              {effectiveTabs.map(t => {
                const cnt = t.id === 'all' ? assets.length : assets.filter(a=>a.fileType===t.id).length;
                const active = tab === t.id;
                return (
                  <button key={t.id} onClick={() => setTab(t.id)} style={{
                    padding:'4px 11px',borderRadius:7,cursor:'pointer',fontSize:11,fontWeight:600,
                    background: active ? 'rgba(139,92,246,0.18)' : 'transparent',
                    border: active ? '1px solid rgba(139,92,246,0.35)' : '1px solid transparent',
                    color: active ? '#a78bfa' : 'var(--t3)',
                    transition:'all 0.18s',
                  }}>
                    {TYPE_ICON[t.id] || '🗂'} {t.label}
                    <span style={{marginLeft:4,opacity:0.55,fontSize:10}}>{cnt}</span>
                  </button>
                );
              })}
            </div>
            <div style={{display:'flex',gap:5,alignItems:'center'}}>
              {selCount > 0 && (
                <>
                  <span style={{fontSize:10,color:'#a78bfa',fontWeight:600}}>{selCount} selected</span>
                  <button onClick={() => handleDelete(Array.from(selected))} disabled={deleting} style={{
                    padding:'4px 10px',borderRadius:6,cursor:'pointer',
                    background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.25)',
                    color:'#f87171',fontSize:10,fontWeight:600,
                  }}>{deleting?'…':'Delete'}</button>
                  <button onClick={() => setSelected(new Set())} style={{
                    padding:'4px 8px',borderRadius:6,cursor:'pointer',
                    ...glass,color:'var(--t3)',fontSize:10,
                  }}>Clear</button>
                </>
              )}
              {filtered.length > 0 && selCount === 0 && (
                <button onClick={selectAll} style={{
                  padding:'4px 10px',borderRadius:6,cursor:'pointer',
                  ...glass,color:'var(--t3)',fontSize:10,
                }}>Select All</button>
              )}
            </div>
          </div>

          {/* ── Grid area ── */}
          <div
            style={{flex:1,overflowY:'auto',padding:'14px 16px',minHeight:0,userSelect:'none'}}
            onMouseUp={() => { isDragging.current = false; }}
            onMouseLeave={() => { isDragging.current = false; }}
            onDragOver={e=>{e.preventDefault();setDragOver(true);}}
            onDragLeave={()=>setDragOver(false)}
            onDrop={e=>{e.preventDefault();setDragOver(false);handleFiles(e.dataTransfer.files);}}
          >
            {dragOver && (
              <div style={{
                position:'absolute',inset:0,zIndex:10,borderRadius:22,
                background:'rgba(139,92,246,0.12)',border:'2px dashed #7c3aed',
                display:'flex',alignItems:'center',justifyContent:'center',pointerEvents:'none',
              }}>
                <span style={{color:'#a78bfa',fontSize:16,fontWeight:700}}>Drop to upload</span>
              </div>
            )}

            {loading ? (
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',gap:14,color:'var(--t3)'}}>
                <div style={{width:36,height:36,border:'3px solid rgba(124,58,237,0.15)',borderTop:'3px solid #7c3aed',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/>
                <span style={{fontSize:12}}>Loading library…</span>
              </div>
            ) : filtered.length === 0 ? (
              <div onClick={() => fileInputRef.current?.click()} style={{
                display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
                height:'100%',gap:12,cursor:'pointer',
                border:'2px dashed rgba(255,255,255,0.08)',borderRadius:16,
                color:'var(--t3)',transition:'border-color 0.2s',
              }}>
                <div style={{fontSize:40}}>☁️</div>
                <div style={{fontSize:13,fontWeight:600,color:'var(--t2)'}}>No {tab==='all'?'':tab+' '}files yet</div>
                <div style={{fontSize:11}}>Click or drag & drop to upload</div>
              </div>
            ) : (
              <div style={{
                display:'grid',
                gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',
                gap:10,
              }}>
                {filtered.map(asset => {
                  const isSel = selected.has(asset.id);
                  const isRen = renaming?.id === asset.id;
                  return (
                    <div
                      key={asset.id}
                      className="mlm-card"
                      onClick={() => toggleSelect(asset)}
                      onMouseDown={() => {
                        isDragging.current = true;
                        dragMode.current = selected.has(asset.id) ? 'remove' : 'add';
                      }}
                      onMouseEnter={() => {
                        if (!isDragging.current) return;
                        setSelected(prev => {
                          const next = new Set(prev);
                          if (dragMode.current === 'remove') { next.delete(asset.id); return next; }
                          if (next.size >= maxSelect) return prev;
                          next.add(asset.id);
                          return next;
                        });
                      }}
                      style={{
                        position:'relative',borderRadius:10,overflow:'hidden',cursor:'pointer',
                        border: isSel ? '2px solid #7c3aed' : '2px solid rgba(255,255,255,0.06)',
                        background: isSel ? 'rgba(124,58,237,0.08)' : 'rgba(255,255,255,0.025)',
                        transition:'border-color 0.15s,box-shadow 0.15s,transform 0.15s',
                        boxShadow: isSel ? '0 0 0 3px rgba(124,58,237,0.2)' : 'none',
                      }}
                    >
                      {/* Thumbnail */}
                      <div style={{width:'100%',paddingBottom:'80%',position:'relative',background:'rgba(0,0,0,0.2)'}}>
                        {asset.fileType === 'image' && (
                          <img src={asset.fileUrl} alt={asset.fileName}
                            style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}} />
                        )}
                        {asset.fileType === 'video' && (
                          <video src={asset.fileUrl} muted
                            style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}} />
                        )}
                        {asset.fileType === 'audio' && (
                          <div style={{
                            position:'absolute',inset:0,
                            background:`linear-gradient(135deg,${TYPE_CLR.audio},rgba(0,0,0,0.1))`,
                            display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:4,
                          }}>
                            <div style={{fontSize:22}}>🎵</div>
                            <div style={{fontSize:8,color:'var(--t3)',letterSpacing:'0.06em',fontWeight:600}}>AUDIO</div>
                          </div>
                        )}

                        {/* Type badge */}
                        <div style={{
                          position:'absolute',bottom:5,left:5,
                          padding:'2px 5px',borderRadius:4,
                          background:'rgba(0,0,0,0.55)',backdropFilter:'blur(6px)',
                          fontSize:8,color:'rgba(255,255,255,0.7)',fontWeight:600,letterSpacing:'0.04em',
                          textTransform:'uppercase',
                        }}>{asset.fileType}</div>

                        {/* Checkbox */}
                        <div style={{
                          position:'absolute',top:5,left:5,
                          width:17,height:17,borderRadius:5,
                          background: isSel ? '#7c3aed' : 'rgba(0,0,0,0.55)',
                          border: isSel ? '1.5px solid #a78bfa' : '1.5px solid rgba(255,255,255,0.25)',
                          display:'flex',alignItems:'center',justifyContent:'center',
                          transition:'all 0.15s',backdropFilter:'blur(4px)',
                        }}>
                          {isSel && <span style={{color:'#fff',fontSize:9,fontWeight:800}}>✓</span>}
                        </div>

                        {/* Hover actions */}
                        <div className="mlm-hover" style={{
                          position:'absolute',inset:0,
                          background:'rgba(0,0,0,0.58)',backdropFilter:'blur(3px)',
                          display:'flex',alignItems:'center',justifyContent:'center',gap:5,
                          opacity:0,transition:'opacity 0.18s',
                        }}>
                          <button
                            onClick={e=>{e.stopPropagation();setPreview(asset);}}
                            title="Preview"
                            style={{
                              width:28,height:28,borderRadius:7,cursor:'pointer',
                              border:'1px solid rgba(255,255,255,0.18)',
                              background:'rgba(255,255,255,0.12)',color:'#e2e8f0',
                              display:'flex',alignItems:'center',justifyContent:'center',
                              transition:'background 0.15s',
                            }}
                          ><EyeIcon /></button>
                          <button
                            onClick={e=>{e.stopPropagation();setRenaming({id:asset.id,value:asset.fileName});}}
                            title="Rename"
                            style={{
                              width:28,height:28,borderRadius:7,cursor:'pointer',
                              border:'1px solid rgba(255,255,255,0.18)',
                              background:'rgba(255,255,255,0.12)',color:'#e2e8f0',
                              display:'flex',alignItems:'center',justifyContent:'center',
                              transition:'background 0.15s',
                            }}
                          ><PenIcon /></button>
                          <button
                            onClick={e=>{e.stopPropagation();handleDelete([asset.id]);}}
                            title="Delete"
                            style={{
                              width:28,height:28,borderRadius:7,cursor:'pointer',
                              border:'1px solid rgba(239,68,68,0.35)',
                              background:'rgba(239,68,68,0.18)',color:'#f87171',
                              display:'flex',alignItems:'center',justifyContent:'center',
                              transition:'background 0.15s',
                            }}
                          ><BinIcon /></button>
                        </div>
                      </div>

                      {/* File info */}
                      <div style={{padding:'5px 7px 6px'}}>
                        {isRen ? (
                          <input autoFocus value={renaming.value}
                            onChange={e=>setRenaming(r=>({...r,value:e.target.value}))}
                            onBlur={saveRename}
                            onKeyDown={e=>{if(e.key==='Enter')saveRename();if(e.key==='Escape')setRenaming(null);}}
                            onClick={e=>e.stopPropagation()}
                            style={{
                              width:'100%',background:'rgba(255,255,255,0.07)',
                              border:'1px solid rgba(139,92,246,0.45)',
                              borderRadius:4,color:'#fff',fontSize:9,padding:'2px 4px',outline:'none',
                            }}
                          />
                        ) : (
                          <div style={{
                            fontSize:9,color:'var(--t2)',fontWeight:500,
                            overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',
                            lineHeight:1.3,
                          }} title={asset.fileName}>{asset.fileName}</div>
                        )}
                        <div style={{fontSize:8,color:'var(--t3)',marginTop:2,display:'flex',gap:4}}>
                          <span>{fmtSize(asset.size)}</span>
                          <span style={{opacity:0.4}}>·</span>
                          <span>{fmtDate(asset.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div style={{
            padding:'11px 18px',
            borderTop:'1px solid rgba(255,255,255,0.055)',
            display:'flex',alignItems:'center',justifyContent:'space-between',
            flexShrink:0,gap:10,
            background:'rgba(124,58,237,0.025)',
          }}>
            <div style={{fontSize:10,color:'var(--t3)'}}>
              {selCount > 0
                ? `${selCount} of ${maxSelect} selected`
                : `${filtered.length} items · Click to select · Hover for actions`}
            </div>
            <div style={{display:'flex',gap:7}}>
              <button onClick={onClose} style={{
                padding:'7px 16px',borderRadius:8,cursor:'pointer',
                ...glass,color:'var(--t2)',fontSize:12,border:'1px solid rgba(255,255,255,0.1)',
              }}>Cancel</button>
              <button onClick={handleUseSelected} disabled={selCount===0} style={{
                padding:'7px 18px',borderRadius:8,fontSize:12,fontWeight:600,
                cursor: selCount===0 ? 'not-allowed' : 'pointer',
                background: selCount===0 ? 'rgba(139,92,246,0.15)' : 'linear-gradient(135deg,#7c3aed,#6d28d9)',
                border:'none',color:'#fff',
                boxShadow: selCount===0 ? 'none' : '0 4px 14px rgba(124,58,237,0.4)',
                opacity: selCount===0 ? 0.5 : 1,
                transition:'all 0.2s',
              }}>
                Use Selected {selCount>0 ? `(${selCount})` : ''}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" multiple
        accept={allowedTypes ? allowedTypes.map(t=>ACCEPTED[t]).join(',') : ACCEPTED.all}
        style={{display:'none'}}
        onChange={e=>{handleFiles(e.target.files);e.target.value='';}}
      />

      {/* Styles */}
      <style>{`
        .mlm-card { transition: border-color 0.15s, box-shadow 0.15s, transform 0.12s; }
        .mlm-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.45) !important; }
        .mlm-card:hover .mlm-hover { opacity: 1 !important; }
        .mlm-hover button:hover { filter: brightness(1.3); }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
